import {
  AlertTriangle,
  ArrowRightLeft,
  BadgeAlert,
  Building2,
  BarChart3,
  Bell,
  BookOpen,
  Brain,
  Calendar,
  CalendarDays,
  Grid3X3,
  Plane,
  ClipboardCheck,
  FileHeart,
  FileCheck,
  GraduationCap,
  HeartHandshake,
  LayoutDashboard,
  MessageSquare,
  MessageSquareText,
  FlaskConical,
  Microscope,
  NotebookPen,
  Settings2,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Stethoscope,
  Upload,
  User,
  UserRoundCheck,
  Users,
  Zap,
  ClipboardList,
  TrendingUp,
  FileSpreadsheet
} from 'lucide-react';

export const navigationModules = [
  {
    id: 'home',
    label: 'Home',
    subtitle: 'Platform entry and status',
    roles: ['super_admin', 'tenant_admin', 'clerk'],
    items: [
      {
        path: '/dashboard',
        label: 'Overview',
        icon: LayoutDashboard,
        roles: ['super_admin', 'tenant_admin', 'clerk'],
        exact: true,
        testId: 'nav-overview',
      },
    ],
  },
  {
    id: 'attendance',
    label: 'Attendance',
    subtitle: 'Teaching sessions and attendance operations',
    roles: ['super_admin', 'tenant_admin', 'clerk', 'super_clerk', 'faculty', 'hod'],
    items: [
      {
        path: '/dashboard/sessions',
        label: 'Sessions',
        icon: CalendarDays,
        roles: ['super_admin', 'tenant_admin', 'clerk', 'super_clerk', 'hod', 'faculty'],
        // Faculty only when designated academic coordinator
        isVisible: (user) => user?.role !== 'faculty' || user?.is_academic_coordinator === true,
        exact: true,
        testId: 'nav-sessions',
      },
      {
        path: '/dashboard/sessions/import',
        label: 'Import Sessions',
        icon: FileSpreadsheet,
        roles: ['super_admin', 'tenant_admin'],
        testId: 'nav-sessions-import',
      },
      {
        path: '/dashboard/attendance-grid',
        label: 'Attendance Grid',
        icon: Grid3X3,
        roles: ['super_admin', 'tenant_admin', 'clerk', 'faculty'],
        isVisible: (user) => user?.role !== 'faculty' || user?.is_academic_coordinator === true,
        testId: 'nav-attendance-grid',
      },
      {
        path: '/dashboard/attendance',
        label: 'Mark Attendance',
        icon: ClipboardCheck,
        roles: ['super_admin', 'tenant_admin', 'clerk', 'faculty'],
        isVisible: (user) => user?.role !== 'faculty' || user?.is_academic_coordinator === true,
        testId: 'nav-mark-attendance',
      },
      {
        path: '/dashboard/attendance-summary',
        label: 'Attendance Summary',
        icon: BookOpen,
        roles: ['super_admin', 'tenant_admin', 'hod', 'super_clerk'],
        matchPrefixes: ['/dashboard/attendance-summary'],
        testId: 'nav-attendance-summary',
      },

      {
        path: '/dashboard/low-attendance',
        label: 'Low Attendance',
        icon: AlertTriangle,
        roles: ['super_admin', 'tenant_admin', 'hod', 'super_clerk'],
        testId: 'nav-low-attendance',
      },
      // {
      //   path: '/dashboard/attendance-alerts',
      //   label: 'Attendance Alerts',
      //   icon: Bell,
      //   roles: ['super_admin', 'tenant_admin', 'faculty'],
      //   testId: 'nav-attendance-alerts',
      // },
      {
        path: '/dashboard/disputes',
        label: 'Disputes',
        icon: MessageSquare,
        roles: ['super_admin', 'tenant_admin'],
        testId: 'nav-disputes',
      },
      {
        path: '/dashboard/analytics',
        label: 'Student Overview',
        icon: BarChart3,
        roles: ['super_admin', 'tenant_admin', 'hod', 'super_clerk'],
        testId: 'nav-analytics',
      },
      {
        path: '/dashboard/leave',
        label: 'Leave Management',
        icon: Plane,
        roles: ['super_admin', 'tenant_admin', 'faculty'],
        testId: 'nav-leave',
      },
      // {
      //   path: '/dashboard/timetable',
      //   label: 'Timetable Builder',
      //   icon: Grid3X3,
      //   roles: ['super_admin', 'tenant_admin'],
      //   testId: 'nav-timetable',
      // },
    ],
  },
  {
    id: 'academic_assessment',
    label: 'Academic & Assessment',
    subtitle: 'Student records, progression, and future exam workflows',
    roles: ['super_admin', 'tenant_admin'],
    items: [
      {
        path: '/dashboard/lifecycle',
        label: 'Year-End Protocol',
        icon: ArrowRightLeft,
        roles: ['super_admin', 'tenant_admin'],
        testId: 'nav-year-end-protocol',
      },
      {
        path: '/dashboard/backlogs',
        label: 'Backlog Tracker',
        icon: AlertTriangle,
        roles: ['super_admin', 'tenant_admin', 'hod'],
        testId: 'nav-backlogs',
      },
      {
        path: '/dashboard/eligibility',
        label: 'Eligibility',
        icon: FileCheck,
        roles: ['super_admin', 'tenant_admin'],
        testId: 'nav-eligibility',
      },
      {
        path: '/dashboard/ia-tracker',
        label: 'IA Tracker',
        icon: ClipboardCheck,
        roles: ['super_admin', 'tenant_admin', 'faculty'],
        testId: 'nav-ia-tracker',
      },
      {
        path: '/dashboard/ia-overview',
        label: 'IA Overview',
        icon: BarChart3,
        roles: ['super_admin', 'tenant_admin', 'hod', 'faculty'],
        testId: 'nav-ia-overview',
      },
      {
        path: '/dashboard/early-warning',
        label: 'Early Warning',
        icon: ShieldAlert,
        roles: ['super_admin', 'tenant_admin'],
        testId: 'nav-early-warning',
      },
    ],
    comingSoon: ['University Exams', 'Logbook'],
  },
  {
    id: 'electives',
    label: 'Electives',
    subtitle: 'Schedule events, manage subjects, and track registrations',
    roles: ['super_admin', 'tenant_admin', 'student'],
    items: [
      {
        path: '/dashboard/elective-bulk-schedule',
        label: 'Schedule Sessions',
        icon: Calendar,
        roles: ['super_admin', 'tenant_admin'],
        testId: 'nav-elective-bulk-schedule',
      },
      {
        path: '/dashboard/elective-schedule',
        label: 'Schedules',
        icon: Calendar,
        roles: ['super_admin', 'tenant_admin'],
        testId: 'nav-elective-schedule',
      },
      {
        path: '/dashboard/elective-attendance',
        label: 'Attendance',
        icon: ClipboardList,
        roles: ['super_admin', 'tenant_admin', 'faculty'],
        testId: 'nav-elective-attendance',
      },
      {
        path: '/dashboard/elective-attendance-summary',
        label: 'Attendance Summary',
        icon: BarChart3,
        roles: ['super_admin', 'tenant_admin'],
        testId: 'nav-elective-attendance-summary',
      },
      {
        path: '/dashboard/elective-low-attendance',
        label: 'Low Attendance',
        icon: TrendingUp,
        roles: ['super_admin', 'tenant_admin'],
        testId: 'nav-elective-low-attendance',
      },
      {
        path: '/dashboard/elective-marks',
        label: 'Marks',
        icon: GraduationCap,
        roles: ['super_admin', 'tenant_admin', 'faculty'],
        testId: 'nav-elective-marks',
      },
      {
        path: '/dashboard/elective-events/admin',
        label: 'Elective Events',
        icon: Zap,
        roles: ['super_admin', 'tenant_admin'],
        matchPrefixes: ['/dashboard/elective-events/admin'],
        testId: 'nav-elective-events-admin',
      },
      // { // TO DO CHECK LATER
      //   path: '/dashboard/elective-events/programmes',
      //   label: 'Programmes',
      //   icon: GraduationCap,
      //   roles: ['super_admin', 'tenant_admin'],
      //   testId: 'nav-elective-programmes',
      // },
      {
        path: '/dashboard/elective-events/subjects',
        label: 'Subjects',
        icon: BookOpen,
        roles: ['super_admin', 'tenant_admin'],
        testId: 'nav-elective-subjects',
      },
      // { // TO DO CHECK LATER
      //   path: '/dashboard/elective-subject-import',
      //   label: 'Import Catalogue',
      //   icon: Upload,
      //   roles: ['super_admin', 'tenant_admin'],
      //   testId: 'nav-elective-subject-import',
      // },
      {
        path: '/dashboard/elective-enrollments',
        label: 'Enrollments',
        icon: Users,
        roles: ['super_admin', 'tenant_admin'],
        testId: 'nav-elective-enrollments',
      },
      {
        path: '/dashboard/elective-disputes',
        label: 'Disputes',
        icon: MessageSquare,
        roles: ['super_admin', 'tenant_admin'],
        testId: 'nav-elective-disputes',
      },
      {
        path: '/dashboard/elective-events/register',
        label: 'Register',
        icon: Zap,
        roles: ['student'],
        testId: 'nav-elective-events-register',
      },
      {
        path: '/dashboard/elective-events/my-electives',
        label: 'My Electives',
        icon: BookOpen,
        roles: ['student'],
        testId: 'nav-my-electives',
      },
    ],
  },
  {
    id: 'faculty_management',
    label: 'Faculty Management Suite',
    subtitle: 'Sessions, leave, and admin oversight',
    roles: ['super_admin', 'tenant_admin', 'faculty', 'hod'],
    items: [
      {
        path: '/dashboard/faculty/dashboard',
        label: 'My Dashboard',
        icon: LayoutDashboard,
        roles: ['faculty'],
        testId: 'nav-faculty-dashboard',
      },
      {
        path: '/dashboard/faculty/me',
        label: 'My Profile',
        icon: User,
        roles: ['faculty'],
        testId: 'nav-faculty-profile',
      },
      {
        path: '/dashboard/faculty/sessions',
        label: 'My Sessions',
        icon: CalendarDays,
        roles: ['faculty'],
        testId: 'nav-faculty-sessions',
      },
      {
        path: '/dashboard/faculty/leave',
        label: 'My Leave',
        icon: Plane,
        roles: ['faculty'],
        testId: 'nav-faculty-leave-self',
      },
      {
        path: '/dashboard/faculty/inbox',
        label: 'My Notifications',
        icon: Bell,
        roles: ['faculty'],
        testId: 'nav-faculty-inbox',
      },
      {
        path: '/dashboard/faculty/report',
        label: 'Session Report',
        icon: ClipboardList,
        roles: ['super_admin', 'tenant_admin'],
        testId: 'nav-faculty-report',
      },
      {
        path: '/dashboard/faculty/leave',
        label: 'Faculty Leave',
        icon: Plane,
        roles: ['super_admin', 'tenant_admin', 'hod'],
        testId: 'nav-faculty-leave',
      },
      {
        path: '/dashboard/faculty/leave-types',
        label: 'Leave Types',
        icon: Settings2,
        roles: ['super_admin', 'tenant_admin'],
        testId: 'nav-faculty-leave-types',
      },
      {
        path: '/dashboard/faculty/conferences',
        label: 'Conferences & CPD',
        icon: GraduationCap,
        roles: ['faculty', 'super_admin', 'tenant_admin', 'hod'],
        testId: 'nav-faculty-conferences',
      },
      {
        path: '/dashboard/faculty/oversight',
        label: 'Faculty Oversight',
        icon: BarChart3,
        roles: ['super_admin', 'tenant_admin', 'hod'],
        testId: 'nav-faculty-oversight',
      },
      {
        path: '/dashboard/faculty/directory',
        label: 'Faculty Directory',
        icon: Users,
        roles: ['super_admin', 'tenant_admin', 'clerk', 'hod'],
        testId: 'nav-faculty-directory',
      },
      {
        path: '/dashboard/faculty/import',
        label: 'Import Faculty',
        icon: FileSpreadsheet,
        roles: ['super_admin', 'tenant_admin'],
        testId: 'nav-faculty-import',
      },
      {
        path: '/dashboard/hod-import',
        label: 'Import HODs',
        icon: Upload,
        roles: ['super_admin', 'tenant_admin'],
        testId: 'nav-hod-import',
      },
      {
        path: '/dashboard/clerk-import',
        label: 'Import Clerks',
        icon: Upload,
        roles: ['super_admin', 'tenant_admin'],
        testId: 'nav-clerk-import',
      },
      {
        path: '/dashboard/faculty/effectiveness',
        label: 'Effectiveness',
        icon: TrendingUp,
        roles: ['super_admin', 'tenant_admin', 'hod'],
        testId: 'nav-faculty-effectiveness',
      },
    ],
  },
  {
    id: 'prac_rotation',
    label: 'Clinical Rotations',
    subtitle: 'Auto-scheduled practical rotation schedules',
    roles: ['super_admin', 'tenant_admin', 'clerk', 'super_clerk', 'hod', 'student'],
    items: [
      {
        path: '/dashboard/my-rotation-schedule',
        label: 'My Rotation',
        icon: Calendar,
        roles: ['student'],
        testId: 'nav-my-rotation',
      },
      {
        path: '/dashboard/rotation-today',
        label: "Today's Marking",
        icon: Calendar,
        roles: ['super_admin', 'tenant_admin', 'clerk', 'hod'],
        testId: 'nav-rotation-today',
      },
      {
        path: '/dashboard/rotation-plans',
        label: 'Rotation Planner',
        icon: Stethoscope,
        roles: ['super_admin', 'tenant_admin', 'hod', 'super_clerk'],
        matchPrefixes: ['/dashboard/rotation-plans'],
        testId: 'nav-rotation-plans',
      },
      {
        path: '/dashboard/rotation-sessions',
        label: 'Rotation Sessions',
        icon: Stethoscope,
        roles: ['super_admin', 'tenant_admin', 'clerk', 'hod'],
        testId: 'nav-rotation-sessions',
      },
      {
        path: '/dashboard/rotation-attendance',
        label: 'Rotation Attendance',
        icon: Stethoscope,
        roles: ['super_admin', 'tenant_admin', 'clerk', 'hod'],
        testId: 'nav-rotation-attendance',
      },
      {
        path: '/dashboard/rotation-attendance-summary',
        label: 'Rotation Summary',
        icon: Stethoscope,
        roles: ['super_admin', 'tenant_admin', 'clerk', 'super_clerk', 'hod'],
        testId: 'nav-rotation-att-summary',
      },
      {
        path: '/dashboard/rotation-disputes',
        label: 'Rotation Disputes',
        icon: Stethoscope,
        roles: ['super_admin', 'tenant_admin'],
        testId: 'nav-rotation-disputes',
      },
      // Clinical Posting v1 nav items removed — replaced by Rotation v2 above
    ],
  },
  {
    id: 'crmi',
    label: 'CRMI',
    subtitle: 'Compulsory Rotatory Medical Internship',
    roles: ['super_admin', 'tenant_admin', 'clerk', 'super_clerk', 'faculty', 'hod', 'student', 'parent'],
    items: [
      {
        path: '/dashboard/crmi',
        label: 'CRMI Dashboard',
        icon: Stethoscope,
        roles: ['super_admin', 'tenant_admin', 'clerk', 'super_clerk', 'faculty', 'hod'],
        exact: true,
        testId: 'nav-crmi',
      },
      {
        path: '/dashboard/crmi',
        label: 'My Schedule',
        icon: Stethoscope,
        roles: ['student', 'parent'],
        exact: true,
        testId: 'nav-crmi-my-schedule',
      },
      // {
      //   path: '/dashboard/crmi/import',
      //   label: 'Import Students',
      //   icon: Upload,
      //   roles: ['super_admin', 'tenant_admin'],
      //   testId: 'nav-crmi-import',
      // },
      {
        path: '/dashboard/crmi/attendance-summary',
        label: 'Attendance Summary',
        icon: BarChart3,
        roles: ['super_admin', 'tenant_admin', 'clerk', 'super_clerk', 'faculty', 'hod', 'student'],
        testId: 'nav-crmi-attendance-summary',
      },
      {
        path: '/dashboard/crmi-mark-attendance',
        label: 'Mark Attendance',
        icon: ClipboardCheck,
        roles: ['super_admin', 'tenant_admin', 'clerk', 'hod', 'faculty'],
        isVisible: (user) => user?.role !== 'faculty' || user?.is_crmi_coordinator === true,
        testId: 'nav-crmi-mark-attendance',
      },
    ],
  },
  {
    id: 'disciplinary_wellness',
    label: 'Disciplinary & Wellness',
    subtitle: 'Behavior, mentorship, and compliance',
    roles: ['super_admin', 'tenant_admin', 'faculty'],
    items: [
      {
        path: '/dashboard/wellness',
        label: 'Wellness Hub',
        icon: FileHeart,
        roles: ['super_admin', 'tenant_admin', 'faculty'],
        testId: 'nav-wellness-hub',
        exact: true,
      },
      {
        path: '/dashboard/wellness/my-mentees',
        label: 'My Mentees',
        icon: HeartHandshake,
        roles: ['faculty'],
        testId: 'nav-my-mentees',
      },
      {
        path: '/dashboard/wellness/psychiatric-referrals',
        label: 'Psychiatric Referrals',
        icon: Brain,
        roles: ['super_admin', 'tenant_admin', 'faculty'],
        // Faculty only see this when flagged as psychiatrist or mentor
        isVisible: (user) => user?.role !== 'faculty' || Boolean(user?.is_psychiatrist) || Boolean(user?.is_mentor),
        testId: 'nav-psychiatric-referrals',
      },
      {
        path: '/dashboard/wellness/mentors',
        label: 'Mentor Assignments',
        icon: UserRoundCheck,
        roles: ['super_admin', 'tenant_admin'],
        testId: 'nav-mentor-assignments',
      },
      {
        path: '/dashboard/wellness/compliance',
        label: 'Anti-Ragging & Compliance',
        icon: ShieldCheck,
        roles: ['super_admin', 'tenant_admin'],
        testId: 'nav-compliance',
      },
    ],
  },
  {
    id: 'administration',
    label: 'Administration',
    subtitle: 'System setup and governance',
    roles: ['super_admin', 'tenant_admin', 'clerk'],
    items: [
      {
        path: '/dashboard/admin/fee-defaulters',
        label: 'Fee Defaulters',
        icon: BadgeAlert,
        roles: ['super_admin', 'tenant_admin'],
        testId: 'nav-fee-defaulters',
      },
      {
        path: '/dashboard/academic-setup',
        label: 'Academic Setup',
        icon: GraduationCap,
        roles: ['super_admin', 'tenant_admin'],
        testId: 'nav-academic-setup',
      },
      {
        path: '/dashboard/notifications',
        label: 'Email & SMS Templates',
        icon: MessageSquareText,
        roles: ['super_admin', 'tenant_admin'],
        testId: 'nav-notifications',
      },
      {
        path: '/dashboard/audit',
        label: 'Audit & Compliance',
        icon: Shield,
        roles: ['super_admin', 'tenant_admin'],
        testId: 'nav-audit-compliance',
      },
      {
        path: '/dashboard/settings/branding',
        label: 'PDF Branding',
        icon: Building2,
        roles: ['super_admin', 'tenant_admin'],
        testId: 'nav-branding',
      },
    ],
  },
  {
    id: 'feedback',
    label: 'Feedback',
    subtitle: 'Anonymous course and faculty feedback forms',
    roles: ['super_admin', 'tenant_admin', 'faculty', 'hod', 'student', 'parent', 'clerk'],
    items: [
      {
        path: '/dashboard/feedback',
        label: 'Manage Feedback',
        icon: BookOpen,
        roles: ['super_admin', 'tenant_admin'],
        matchPrefixes: ['/dashboard/feedback'],
        testId: 'nav-feedback-admin',
      },
      {
        path: '/dashboard/feedback/my-forms',
        label: 'My Forms',
        icon: BookOpen,
        roles: ['student', 'faculty', 'hod', 'parent', 'clerk'],
        testId: 'nav-feedback-respondent',
      },
      {
        path: '/dashboard/feedback/my-results',
        label: 'My Results',
        icon: BookOpen,
        roles: ['faculty', 'hod'],
        testId: 'nav-feedback-faculty',
      },
    ],
  },
  {
    id: 'outreach',
    label: 'Outreach',
    subtitle: 'Manage institutional activities and OD attendance',
    roles: ['super_admin', 'tenant_admin', 'faculty', 'student'],
    items: [
      {
        path: '/dashboard/outreach',
        label: 'Activities',
        icon: BookOpen,
        roles: ['super_admin', 'tenant_admin', 'faculty'],
        matchPrefixes: ['/dashboard/outreach'],
        testId: 'nav-outreach-admin',
      },
      {
        path: '/dashboard/outreach/my-activities',
        label: 'My Activities',
        icon: BookOpen,
        roles: ['student'],
        testId: 'nav-outreach-student',
      },
    ],
  },
  {
    id: 'pg',
    label: 'PG Residents',
    subtitle: 'Postgraduate activity logbook and research tracking',
    roles: ['super_admin', 'tenant_admin', 'faculty', 'hod', 'clerk', 'super_clerk', 'student'],
    items: [
      {
        path: '/dashboard/pg',
        label: 'Residents',
        icon: Microscope,
        roles: ['super_admin', 'tenant_admin', 'faculty', 'hod'],
        // Dual-role faculty (Junior Residents / Tutors marked as PG) get their
        // own logbook instead of the staff roster
        isVisible: (user) => !(user?.role === 'faculty' && user?.is_pg_resident),
        exact: true,
        matchPrefixes: ['/dashboard/pg/students'],
        testId: 'nav-pg-residents',
      },
      {
        path: '/dashboard/pg/sessions',
        label: 'PG Sessions',
        icon: CalendarDays,
        roles: ['super_admin', 'tenant_admin', 'faculty', 'hod', 'clerk', 'super_clerk', 'student'],
        isVisible: (user) => user?.role !== 'student' || user?.is_pg === true,
        testId: 'nav-pg-sessions',
      },
      {
        path: '/dashboard/pg/my',
        label: 'My PG Logbook',
        icon: NotebookPen,
        roles: ['student', 'faculty'],
        isVisible: (user) => user?.role === 'student' || user?.is_pg_resident === true,
        testId: 'nav-pg-student',
      },
    ],
  },
  {
    id: 'research',
    label: 'Research',
    subtitle: 'Department research activity register',
    roles: ['super_admin', 'tenant_admin', 'hod', 'faculty', 'student'],
    items: [
      {
        path: '/dashboard/research',
        label: 'Research Activities',
        icon: FlaskConical,
        roles: ['super_admin', 'tenant_admin', 'hod', 'faculty'],
        // Faculty only via coordinator designations (read-only register)
        isVisible: (user) => user?.role !== 'faculty'
          || user?.is_research_coordinator === true
          || user?.is_innovation_council === true,
        exact: true,
        testId: 'nav-research-admin',
      },
      {
        path: '/dashboard/research/submit',
        label: 'Submit Research',
        icon: NotebookPen,
        roles: ['faculty', 'hod', 'student'],
        testId: 'nav-research-submit',
      },
    ],
  },
  {
    id: 'holiday_calendar',
    label: 'Calendar',
    subtitle: 'Holidays and institution calendar',
    roles: ['super_admin', 'tenant_admin', 'clerk', 'faculty', 'student', 'parent'],
    items: [
      {
        path: '/dashboard/holiday-calendar',
        label: 'Holiday Calendar',
        icon: CalendarDays,
        roles: ['super_admin', 'tenant_admin', 'clerk', 'faculty', 'student', 'parent'],
        testId: 'nav-holiday-calendar',
      },
    ],
  },
];

export const roleLabels = {
  super_admin: 'Super Admin',
  tenant_admin: 'College Admin',
  clerk: 'Clerk',
  faculty: 'Faculty',
  hod: 'Head of Dept',
  student: 'Student',
  parent: 'Parent',
};

export function isItemActive(locationPath, item) {
  if (item.exact) {
    return locationPath === item.path;
  }

  const candidates = item.matchPrefixes || [item.path];
  return candidates.some((candidate) => locationPath === candidate || locationPath.startsWith(`${candidate}/`));
}

// Dynamic student/parent portal nav (paths depend on user.student_uuid)
export function buildStudentPortalModule(user) {
  const isStudentOrParent = user?.role === 'student' || user?.role === 'parent';
  if (!isStudentOrParent || !user?.student_uuid) return null;

  return {
    id: 'my_portal',
    label: user.role === 'parent' ? "Child's Portal" : 'My Portal',
    subtitle: user.role === 'parent' ? "Your child's academic profile" : 'Your academic profile',
    items: [
      {
        path: `/dashboard/students/${user.student_uuid}`,
        label: 'Profile',
        icon: User,
        roles: [user.role],
        testId: 'nav-my-profile',
        exact: true,
      },
      {
        path: `/dashboard/students/${user.student_uuid}/attendance`,
        label: 'Attendance',
        icon: CalendarDays,
        roles: [user.role],
        testId: 'nav-my-attendance',
      },
      {
        path: `/dashboard/students/${user.student_uuid}/classes`,
        label: 'Classes',
        icon: BookOpen,
        roles: [user.role],
        testId: 'nav-my-classes',
      },
      ...(user.role === 'student' ? [{
        path: '/dashboard/my-alerts',
        label: 'My Alerts',
        icon: Bell,
        roles: ['student'],
        testId: 'nav-my-alerts',
      }, {
        path: '/dashboard/leave',
        label: 'Leave',
        icon: Plane,
        roles: ['student'],
        testId: 'nav-my-leave',
      }, {
        path: '/dashboard/wellness/my-records',
        label: 'Mentorship & Counseling',
        icon: HeartHandshake,
        roles: ['student'],
        testId: 'nav-my-wellness',
      }] : []),
      ...(user.role === 'parent' ? [{
        path: '/dashboard/parent-inbox',
        label: 'Notifications',
        icon: Bell,
        roles: ['parent'],
        testId: 'nav-parent-inbox',
      }] : []),
    ],
  };
}
